import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { getTestPerformance } from "../pages/services/performanceService";
import { assignSeriesColors, SERIES_PALETTE_SIZE } from "./utils/seriesColors";
import "../styles/TestPerformance.css";

// "09 Sep · Weekly Test # 1" — the x-axis carries both the date and the title,
// stacked by the custom tick so a long title never squashes the axis.
const axisDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

const AxisTick = ({ x, y, payload, rows }) => {
  const row = rows[payload.index];
  if (!row) return null;
  const title = row.testTitle.length > 22 ? `${row.testTitle.slice(0, 21)}…` : row.testTitle;
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" dy={12} className="tp-axis-date">{axisDate(row.date)}</text>
      <text textAnchor="middle" dy={26} className="tp-axis-title">{title}</text>
    </g>
  );
};

// The hover box the brief asked for: totals with correct/incorrect/unattempted
// colour-coded. One block per student who sat that test.
const ScoreTooltip = ({ active, payload, colors }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="tp-tooltip">
      <p className="tp-tooltip-title">{row.testTitle}</p>
      <p className="tp-tooltip-date">{row.date ? new Date(row.date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : ""}</p>
      {payload.map((entry) => {
        const b = row.breakdown?.[entry.dataKey];
        if (!b) return null;
        return (
          <div className="tp-tooltip-block" key={entry.dataKey}>
            <p className="tp-tooltip-student">
              <span className="tp-swatch" style={{ background: colors[entry.dataKey] }} />
              {entry.name}
              <span className="tp-tooltip-score">{b.score}</span>
            </p>
            <p className="tp-stat">Total Qs: <span>{b.total}</span></p>
            <p className="tp-stat tp-correct">Correct: <span>{b.correct}</span></p>
            <p className="tp-stat tp-incorrect">Incorrect: <span>{b.incorrect}</span></p>
            <p className="tp-stat tp-unattempted">Unattempted: <span>{b.unattempted}</span></p>
          </div>
        );
      })}
    </div>
  );
};

const TestPerformanceChart = ({ filters = {} }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scope = JSON.stringify({
    batchId: filters.batchId || "", studentId: filters.studentId || "",
    from: filters.from || "", to: filters.to || "",
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTestPerformance(JSON.parse(scope))
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [scope]);

  const series = data?.series || [];
  const rows = data?.data || [];
  const colors = useMemo(() => assignSeriesColors(series.map((s) => s.studentId)), [series]);

  // Scores are whole numbers of correct answers, so the axis should be too.
  const maxScore = rows.reduce((m, r) => {
    series.forEach((s) => { if (typeof r[s.studentId] === "number") m = Math.max(m, r[s.studentId]); });
    return m;
  }, 0);

  return (
    <div className="dash-chart-panel tp-panel">
      <div className="tp-panel-head">
        <h3 className="dash-chart-panel-title">Test performance — score per test</h3>
        <button
          type="button"
          className="tp-detail-btn"
          onClick={() => navigate("/test-results")}
        >
          View detailed test results →
        </button>
      </div>
      <p className="tp-panel-sub">1 mark per correct answer. Incorrect and unattempted score 0.</p>

      {loading && <div className="tp-state">Loading test performance…</div>}
      {!loading && error && <div className="tp-state tp-state-error">{error}</div>}
      {!loading && !error && !rows.length && (
        <div className="tp-state">No test attempts yet for this selection.</div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          {series.length > SERIES_PALETTE_SIZE && (
            <p className="tp-warn">
              {series.length} students shown — colours repeat beyond {SERIES_PALETTE_SIZE}.
              Filter by batch or student to keep the lines distinguishable.
            </p>
          )}
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={rows} margin={{ top: 8, right: 20, left: 0, bottom: 28 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis
                dataKey="testId"
                tick={<AxisTick rows={rows} />}
                height={48}
                interval={0}
                stroke="#cbd5e1"
              />
              <YAxis
                allowDecimals={false}
                domain={[0, Math.max(5, maxScore)]}
                stroke="#cbd5e1"
                tick={{ fill: "#64748b", fontSize: 12 }}
                label={{ value: "Score", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 12 }}
              />
              <Tooltip content={<ScoreTooltip colors={colors} />} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              {series.map((s) => (
                <Line
                  key={s.studentId}
                  type="monotone"
                  dataKey={s.studentId}
                  name={s.studentName}
                  stroke={colors[s.studentId]}
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 2, fill: "#fff" }}
                  activeDot={{ r: 6 }}
                  // A student who did not sit a test leaves a gap rather than
                  // dropping to zero, which would read as a score of nothing.
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
};

export default TestPerformanceChart;
